import {
  Component, ElementRef, EventEmitter, Input, NgModule,
  OnChanges, OnInit, Output, Renderer2, SimpleChanges,
  AfterViewChecked, ViewChildren, QueryList, forwardRef
} from '@angular/core';
import {
  CreateControlAccessProvider,
  filterDeep,
  WOption,
  WOptionEvent,
  WOptionOrString,
  WUniversalModule
} from '@wipo/w-angular/shared';
import { FFacetInfoModule } from '@wipo/w-angular/internal';
import { WInputMulticolumns } from '@wipo/w-angular/shared';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WHideEmptyModule } from '@wipo/w-angular/directives';
import { WNestedCheckboxComponent } from './w-nested-checkbox/w-nested-checkbox.component';
import { WInlineFilterModule } from '@wipo/w-angular/w-inline-filter';

const CHECK_HEIGHT = 24;
@Component({
    selector: 'w-input-checkbox-many',
    templateUrl: './w-input-checkbox-many.component.html',
    providers: [CreateControlAccessProvider(WInputCheckboxManyComponent)],
    styles: [`.b-checkmark {border-radius: 3px;}`],
    host: {
        class: 'b-input b-input-checkbox',
        '[class.b-input__large]': 'isLarge'
    },
    standalone: false
})
/*
@id w-input-checkbox-many
@name w-input-checkbox-many
@status release
@description
*/
export class WInputCheckboxManyComponent extends WInputMulticolumns implements OnChanges {
  @ViewChildren(WNestedCheckboxComponent) children!: QueryList<WNestedCheckboxComponent>;

  @Output() wClick = new EventEmitter<WOptionEvent>();  // @no-doc returns Event, clicked option and array of selected values (before change)
  @Output() wChange = new EventEmitter<WOptionEvent>(); // return Event, changed option and array of selected values

  @Input() options?: WOptionOrString[]; // array of either plain values or WOption objects
  @Input() isLarge: boolean = false; // use large checkboxes
  @Input() canCollapse: boolean = false; // use hinges to expand/collapse, if =false, the tree is always expanded

  @Input() showLimit: number = 0; // show ONLY n values; 0 => no limit
  @Input() showLimitAll: string = 'Show all';  // default message, provide customised or translation
  @Input() showLimitLess: string = 'Show less'; // default message, provide customised or translation
  // can enable filter, or set filter string
  @Input() hasFilter: boolean = false; // show filter field
  @Input() filterText: string = ''; // filter text
  @Input() filterPlaceholder: string = 'Search'; // filter prompt, default is 'Search'
  
  showCount = 0; // number of options after limit AND filter

  showAll: boolean = false; // toggled by showAll/Less
  get showLimitMessage() {
    // if no limit or limit larger that number of
    const optionsLength = this.options?.length ?? 0;
    if (!this.showLimit || optionsLength <= this.showCount) { return ''; }
    // else decide to use all or less
    return (optionsLength > this.showCount && !this.showAll) ? this.showLimitAll : this.showLimitLess;
  }

  // implement parent property
  protected get childrenCount() {
    if (this.children) {
      const count = this.children.reduce((h, child, i) => {
        // count only visible lines
        if (this.showOptionByIndex(i)) {
          h++;
        }
        return h;
      }, 0);
      return count;
    } else { return 0; }
  }
  // protected get childHeight(){
  //   return Math.ceil(this.childrenHeight / this.childrenCount);
  // }
  protected get childHeight() {
    if (this.children?.length ?? 0 >0) {
      const r =  this.children?.get(0);
      return r?.height ?? 23;
    } else return 26;
  }
  get childrenHeight() {
    if (this.children) {
      const childrenHeight = this.children.reduce((h, child) => {
        return h + child.height;
      }, 0);
      return childrenHeight;
    } else { return 0; }
  }

  // tslint:disable-next-line: variable-name
  constructor(protected _renderer: Renderer2, protected _elementRef: ElementRef) { super(); }
  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.calculateShownItems();
  }
  getSelected(): WOptionOrString | WOptionOrString[] {
    return this.children.reduce((arr, child) => {
      const selected = child.getSelected();
      if (selected) {
        if (Array.isArray(selected)) {
          arr.splice(arr.length, 0, ...selected);
        } else {
          arr.push(selected);
        }
      }
      return arr;
    }, [] as WOptionOrString[]);
  }
  showOptionByIndex(i: number) {
    return (this.showAll || this.showCount === 0 || this.showCount > i) && (!this.filterText || this.showFilteredOption(i));
  }
  showFilteredOption(i: number) {
    return !!this.filterText && !!this.options && filterDeep(this.options[i], this.filterText.toLowerCase());
  }
  // recalculate on any input changes or filterText change
  calculateShownItems(): void {
    if (!Array.isArray(this.options) || this.showAll || this.showLimit === 0) {
      this.showCount = 0; // no limit
    } else if (!this.filterText) {
      this.showCount = this.showLimit; // limit, but no no filtering
    } else {
      this.showCount = + this.options.reduce((count, o, i) => {
        return count + (i < count && !filterDeep(o, this.filterText.toLowerCase()) ? 1 : 0);
      }, this.showLimit);
    }
  }
  toggleAll() {
    this.showAll = !this.showAll;
  }
  checked(value: string): boolean {
    return Array.isArray(this.value) && this.value.includes(value);
  }
  getSelection(source: WOption | string, event: Event): WOptionEvent {
    return { event, source, value: this.value, selected: this.getSelected() };
  }



  stateChanged(event: WOptionEvent) {
    if (!Array.isArray(this.value)) {
      // init value
      this.value = [...event.value];
    }
    this.wChange.emit({ ...event, selected: this.getSelected() });
  }
}
@NgModule({
  imports: [CommonModule, FormsModule, WHideEmptyModule, FFacetInfoModule, WUniversalModule, WInlineFilterModule],
  exports: [WInputCheckboxManyComponent, WNestedCheckboxComponent],
  declarations: [WInputCheckboxManyComponent, WNestedCheckboxComponent]
})
export class WInputCheckboxManyModule { }
